import { PAYMENT_METHOD } from './payment-method';
import { PRICE_DISPLAY_MODE } from './price-display-mode';
import { QUOTA_PERIOD } from './quota-period';
import { RECURRENCE_INTERVAL } from './recurrence-interval';
import { RECURRING_BOOKING_MODE } from './recurring-booking-mode';

/** Onde o atendimento acontece — a resposta do slot `location` da entrevista. */
export enum PACK_LOCATION_MODE {
  /** O cliente vai até o prestador, num endereço fixo. */
  PROVIDER_SPACE = 'PROVIDER_SPACE',
  /** O prestador vai até o cliente. */
  CLIENT_ADDRESS = 'CLIENT_ADDRESS',
  /** Atendimento remoto. */
  ONLINE = 'ONLINE',
}

export interface PackService {
  name: string;
  description?: string;
  /** Nulo = valor combinado em cada contrato (orçado, sob medida). */
  defaultPrice?: number | null;
  priceDisplayMode?: PRICE_DISPLAY_MODE;
  allowedPaymentMethods?: PAYMENT_METHOD[];
  /** Nulo = serviço sob medida: não aparece na disponibilidade online. */
  durationMinutes?: number | null;
  slotCapacity?: number;
  /** Assinatura: o cliente paga o ciclo e marca dentro da cota do período. */
  isSubscription?: boolean;
  subscriptionQuota?: number | null;
  subscriptionQuotaPeriod?: QUOTA_PERIOD | null;
  recurringBookingMode?: RECURRING_BOOKING_MODE;
  recurringBillingInterval?: RECURRENCE_INTERVAL | null;
  recurringHorizonDays?: number | null;
}

export interface PackAvailabilitySlot {
  /** 0=dom … 6=sáb, como no resto do agendamento. */
  weekday: number;
  startTime: string;
  endTime: string;
}

export interface PackLocation {
  mode: PACK_LOCATION_MODE;
  address?: string | null;
  /** O ponto exato do estabelecimento, marcado no mapa. */
  latitude?: number | null;
  longitude?: number | null;
}

/** As 8 chaves que o `useTerms()` do T-25 vai consumir. */
export interface PackVocabulary {
  client: string;
  clientPlural: string;
  service: string;
  servicePlural: string;
  enrollment: string;
  enrollmentPlural: string;
  appointment: string;
  appointmentPlural: string;
  /** O negócio com equipe: "Barbearia", "Clínica". */
  organization: string;
  /** Quem atende na equipe: "Barbeiro", "Psicóloga". */
  professional: string;
  professionalPlural: string;
}

export interface PackFieldVisibility {
  showRecurrence: boolean;
  showSlotCapacity: boolean;
  showPriceDisplayMode: boolean;
  showFixedLocation: boolean;
}

/**
 * O payload como o rascunho guarda: tudo opcional, porque a entrevista o
 * preenche aos pedaços. O app só lê — quem valida é o zod do backend.
 */
export interface ActivityPackPayloadPatch {
  /** Nome do negócio — vai para o `provider.title` no apply. */
  businessName?: string | null;
  /** Tem mais gente atendendo com ele: o apply liga a organização. */
  hasTeam?: boolean;
  /** Manual ou recebimento automático — preferência, configurada depois. */
  paymentPreference?: 'manual' | 'automatic';
  services?: PackService[];
  availabilityGrid?: PackAvailabilitySlot[];
  location?: Partial<PackLocation>;
  scheduleSettings?: Record<string, unknown>;
  contractTemplates?: unknown[];
  vocabulary?: Partial<PackVocabulary>;
  fieldVisibility?: Partial<PackFieldVisibility>;
}

/** Item da lista de ramos do fallback sem IA (T-24). */
export interface ActivityPackSummary {
  slug: string;
  label: string;
  categoryId: string | null;
  /** Quantos serviços o prestador ganha ao adotar o pack. */
  serviceCount: number;
}

export interface ActivityCategory {
  id: string;
  slug: string;
  label: string;
  icon: string | null;
  packCount: number;
}
